/**
 * WindowPresetBar - Compact window preset buttons
 * Single row of icon buttons for quick preset selection
 */

import React from "react";
import { WINDOW_PRESETS, WindowPreset } from "../../constants/windowPresets";

// Re-export for backwards compatibility
export type { WindowPreset } from "../../constants/windowPresets";

interface WindowPresetBarProps {
  activePreset?: WindowPreset | null;
  onPresetSelect: (preset: WindowPreset, center: number, width: number) => void;
}

export const WindowPresetBar: React.FC<WindowPresetBarProps> = ({
  activePreset,
  onPresetSelect,
}) => {
  return (
    <div className="px-2 py-2 shrink-0">
      {/* Hover-revealed tooltip surfaces every preset shortcut at a glance,
          so radiologists don't have to discover them per-button.
          Native title= was unreliable for multi-line content; this is a
          pure-CSS popup anchored to the label. */}
      <div className="relative group mb-1.5">
        <div className="sidebar-text-label text-center cursor-help inline-flex items-center justify-center gap-1 w-full">
          <span>Presets</span>
          <span className="text-fg-muted text-[9px]" aria-hidden="true">
            ⓘ
          </span>
        </div>
        <div
          role="tooltip"
          className="absolute left-full top-0 ml-2 z-50
                     px-2 py-1.5 rounded shadow-lg
                     bg-bg-2/95 border border-white/10
                     text-[10px] text-fg-muted leading-tight whitespace-nowrap
                     opacity-0 pointer-events-none -translate-x-1
                     group-hover:opacity-100 group-hover:translate-x-0
                     transition-all duration-150"
        >
          {WINDOW_PRESETS.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-3">
              <span className="text-fg">{p.label}</span>
              <span className="text-fg-muted font-mono">
                {p.numberKey} · {p.letterKey}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="sidebar-card rounded-lg p-1.5">
        <div className="grid grid-cols-3 gap-1">
          {WINDOW_PRESETS.map((preset) => (
            <button
              key={preset.id}
              onClick={() => onPresetSelect(preset.id, preset.center, preset.width)}
              className={`
                px-1.5 py-1 rounded text-[8px] font-medium transition-all duration-200
                ${
                  activePreset === preset.id
                    ? "bg-primary/25 text-signal-text-tint ring-1 ring-primary/30"
                    : "text-fg-muted hover:text-white hover:bg-white/10"
                }
              `}
              title={`${preset.label} (W:${preset.width} L:${preset.center}) - ${preset.numberKey} or ${preset.letterKey}`}
            >
              {preset.shortLabel}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WindowPresetBar;

// Re-export presets for backwards compatibility
export { WINDOW_PRESETS as PRESETS };
